<script lang="ts">
	import { theme } from '$lib/stores/theme';
	import type { Theme } from '$lib/stores/theme';
	import Icon from '$lib/components/primitives/Icon.svelte';
	import { browser } from '$app/environment';
	import { fade } from 'svelte/transition';
	import { _ } from 'svelte-i18n';

	// Get initial theme preference synchronously
	function getInitialTheme(): Theme {
		if (browser) {
			const stored = localStorage.getItem('theme') as Theme;
			return stored || 'system';
		}
		return 'system';
	}
	
	// Get the actual rendered theme (what the user sees)
	function getRenderedTheme(): 'light' | 'dark' {
		if (!browser) return 'light';
		
		// Check the actual data-theme attribute set by our inline script
		const dataTheme = document.documentElement.getAttribute('data-theme');
		if (dataTheme === 'dark') return 'dark';
		if (dataTheme === 'light') return 'light';
		
		// Fallback to media query
		return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
	}
	
	// Initialize with actual theme preference
	let currentTheme = $state<Theme>(getInitialTheme());
	
	// Track the actual rendered theme for icon display
	let renderedTheme = $state<'light' | 'dark'>(getRenderedTheme());
	
	// Track if icon is ready to show (to prevent flash)
	let iconReady = $state(false);
	
	// Update when theme changes from store
	$effect(() => {
		if ($theme) {
			currentTheme = $theme;
		}
		// Update rendered theme when theme changes
		if (browser) {
			// Check immediately on mount
			renderedTheme = getRenderedTheme();
			// Mark icon as ready after we know the correct theme
			if (!iconReady) {
				requestAnimationFrame(() => {
					iconReady = true;
				});
			}
		}
	});

	function cycleTheme() {
		const themes: Theme[] = ['light', 'dark', 'system'];
		const currentIndex = themes.indexOf(currentTheme);
		const nextIndex = (currentIndex + 1) % themes.length;
		theme.set(themes[nextIndex]);
	}

	// Show the appropriate icon based on theme setting
	// For "system" mode, show the monitor icon
	const iconName = $derived(
		currentTheme === 'system' 
			? 'monitor'
			: (currentTheme === 'dark' ? 'moon' : 'sun')
	);
	
	const label = $derived(
		currentTheme === 'light' ? $_('theme.light') : 
		currentTheme === 'dark' ? $_('theme.dark') : 
		$_('theme.systemWithState', { values: { mode: renderedTheme === 'dark' ? $_('theme.dark') : $_('theme.light') }})
	);
	
	// Track if we should show transitions (after initial load)
	let enableTransitions = $state(false);
	$effect(() => {
		// Enable transitions after a short delay to prevent initial flash
		const timer = setTimeout(() => {
			enableTransitions = true;
		}, 100);
		return () => clearTimeout(timer);
	});
	
	// Listen for system theme changes
	$effect(() => {
		if (!browser) return;
		
		const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
		const handleChange = () => {
			if (currentTheme === 'system') {
				renderedTheme = getRenderedTheme();
			}
		};
		
		mediaQuery.addEventListener('change', handleChange);
		return () => mediaQuery.removeEventListener('change', handleChange);
	});
</script>

<button
	onclick={cycleTheme}
	class="theme-toggle {enableTransitions ? 'theme-toggle--animated' : ''}"
	aria-label={$_('theme.toggleAriaLabel', { values: { label }})}
	title={label}
	data-testid="theme-toggle"
>
	<div class="icon-wrapper">
		{#if iconReady}
			<div class="icon-content" transition:fade={{ duration: 200 }}>
				<Icon name={iconName} size={20} />
			</div>
		{:else}
			<!-- Placeholder with subtle pulse animation to indicate loading -->
			<div class="icon-placeholder"></div>
		{/if}
	</div>
</button>

<style>
	.theme-toggle {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 40px;
		height: 40px;
		padding: 0;
		background: var(--bg-secondary);
		color: var(--text-primary);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-md);
		cursor: pointer;
		position: relative;
		overflow: hidden;
	}
	
	/* No transitions on initial load */
	.theme-toggle:not(.theme-toggle--animated) {
		transition: none !important;
	}
	
	.theme-toggle:not(.theme-toggle--animated) * {
		transition: none !important;
	}
	
	/* Enable transitions after initial load */
	.theme-toggle--animated {
		transition: var(--transition-colors), var(--transition-transform);
	}
	
	.theme-toggle--animated .icon-wrapper {
		transition: transform 0.2s ease, opacity 0.2s ease;
	}

	.theme-toggle:hover {
		background: var(--bg-tertiary);
		transform: translateY(-1px);
	}

	.theme-toggle:active {
		transform: translateY(0);
	}
	
	.icon-wrapper {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 100%;
		height: 100%;
		position: relative;
	}
	
	.icon-content {
		display: flex;
		align-items: center;
		justify-content: center;
	}
	
	.icon-placeholder {
		width: 12px;
		height: 12px;
		border-radius: var(--radius-full);
		background: linear-gradient(135deg, var(--text-tertiary), var(--text-secondary));
		opacity: 0.3;
		animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
	}
	
	@keyframes pulse {
		0%, 100% {
			opacity: 0.3;
			transform: scale(1);
		}
		50% {
			opacity: 0.5;
			transform: scale(1.1);
		}
	}
	
	/* Add a subtle rotation on theme change */
	.theme-toggle--animated:active .icon-content {
		transform: rotate(180deg) scale(0.8);
		opacity: 0.7;
	}
</style>